import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import salesApiClient from '@/api/salesClient';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { 
  Users, TrendingUp, CheckSquare, Activity, Target, 
  Phone, Mail, Calendar, ArrowRight, Plus, Bot,
  DollarSign, FileText, Clock, BarChart3
} from 'lucide-react';

export default function SalesDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    total_contacts: 0,
    total_leads: 0,
    pending_tasks: 0,
    total_pipeline_value: 0,
    pipeline_stats: {},
    recent_activities: []
  });
  const [myTarget, setMyTarget] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const userData = JSON.parse(localStorage.getItem('userData') || '{}');
  const salesUserStr = localStorage.getItem('salesUser');
  const user = salesUserStr ? JSON.parse(salesUserStr) : userData;

  useEffect(() => {
    fetchStats();
    fetchMyTarget();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await salesApiClient.get('/reports/stats');
      setStats(response.data);
    } catch (error) {
      console.log('Using default stats');
    } finally {
      setLoading(false);
    }
  };

  const fetchMyTarget = async () => {
    try {
      const response = await salesApiClient.get(`/sales-targets/user/${user.id}`);
      const activeTarget = response.data?.find(t => new Date(t.end_date) >= new Date());
      setMyTarget(activeTarget);
    } catch (error) {
      console.log('No target found');
    }
  };

  const statCards = [
    { 
      title: 'Total Contacts', 
      value: stats.total_contacts || 0, 
      icon: Users, 
      color: 'from-blue-500 to-cyan-500',
      bgColor: 'bg-blue-500/10',
      textColor: 'text-blue-400',
      href: '/suite/sales/contacts' 
    },
    { 
      title: 'Active Leads', 
      value: stats.total_leads || 0, 
      icon: TrendingUp, 
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-500/10',
      textColor: 'text-green-400',
      href: '/suite/sales/leads' 
    },
    { 
      title: 'Pending Tasks', 
      value: stats.pending_tasks || 0, 
      icon: CheckSquare, 
      color: 'from-amber-500 to-orange-500',
      bgColor: 'bg-amber-500/10',
      textColor: 'text-amber-400',
      href: '/suite/sales/tasks' 
    },
    { 
      title: 'Pipeline Value', 
      value: `$${(stats.total_pipeline_value || 0).toLocaleString()}`, 
      icon: DollarSign, 
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-500/10',
      textColor: 'text-purple-400',
      href: '/suite/sales/leads' 
    },
  ];

  const quickActions = [
    { label: 'Add Contact', icon: Users, onClick: () => navigate('/suite/sales/contacts'), color: 'bg-blue-600 hover:bg-blue-700' },
    { label: 'New Lead', icon: TrendingUp, onClick: () => navigate('/suite/sales/leads'), color: 'bg-green-600 hover:bg-green-700' },
    { label: 'AI Assistant', icon: Bot, onClick: () => navigate('/suite/sales/ai'), color: 'bg-purple-600 hover:bg-purple-700' },
    { label: 'View Reports', icon: BarChart3, onClick: () => navigate('/suite/sales/reports'), color: 'bg-orange-600 hover:bg-orange-700' },
  ];

  const pipelineStages = [
    { name: 'Prospect', count: stats.pipeline_stats?.prospect || stats.pipeline_stats?.Prospect || 0, color: 'bg-slate-500' },
    { name: 'Contacted', count: stats.pipeline_stats?.contacted || stats.pipeline_stats?.Contacted || 0, color: 'bg-blue-500' },
    { name: 'Negotiation', count: stats.pipeline_stats?.negotiation || stats.pipeline_stats?.Negotiation || 0, color: 'bg-amber-500' },
    { name: 'Contract', count: stats.pipeline_stats?.contract || stats.pipeline_stats?.Contract || 0, color: 'bg-purple-500' },
    { name: 'Closed', count: stats.pipeline_stats?.closed || stats.pipeline_stats?.Closed || 0, color: 'bg-green-500' },
  ];

  const totalPipeline = pipelineStages.reduce((sum, s) => sum + s.count, 0) || 1;

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-white">Welcome back, {user.name || 'User'}!</h1>
          <p className="text-slate-400 mt-1">Here's your sales overview</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/suite/sales/contacts')} className="bg-orange-600 hover:bg-orange-700">
            <Plus className="w-4 h-4 mr-2" />
            Add Contact
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {statCards.map((stat) => (
          <Card 
            key={stat.title}
            className="bg-slate-900 border-slate-800 cursor-pointer hover:border-slate-600 transition-all hover:scale-[1.02]"
            onClick={() => navigate(stat.href)}
          >
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-400 uppercase tracking-wider">{stat.title}</p>
                  <p className="text-3xl font-bold text-white mt-2">{stat.value}</p>
                </div>
                <div className={`w-14 h-14 rounded-2xl ${stat.bgColor} flex items-center justify-center`}>
                  <stat.icon className={`w-7 h-7 ${stat.textColor}`} />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* My Sales Target */}
      {myTarget && (
        <Card className="bg-gradient-to-r from-orange-900/30 to-amber-900/30 border-orange-800/50">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 rounded-2xl bg-orange-500/20 flex items-center justify-center">
                  <Target className="w-7 h-7 text-orange-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white">My Sales Target</h3>
                  <p className="text-sm text-slate-400">{myTarget.period} - Ends {new Date(myTarget.end_date).toLocaleDateString()}</p>
                </div>
              </div>
              <div className="flex-1 max-w-md">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-slate-400">Progress</span>
                  <span className="text-white font-semibold">
                    ${myTarget.achieved_amount?.toLocaleString()} / ${myTarget.target_amount?.toLocaleString()}
                  </span>
                </div>
                <Progress 
                  value={(myTarget.achieved_amount / myTarget.target_amount) * 100} 
                  className="h-3 bg-slate-700"
                />
                <p className="text-right text-sm text-orange-400 mt-1">
                  {((myTarget.achieved_amount / myTarget.target_amount) * 100).toFixed(1)}% achieved
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Pipeline Overview */}
        <Card className="lg:col-span-2 bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Activity className="w-5 h-5 text-orange-400" />
              Pipeline Overview
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-1 h-4 rounded-full overflow-hidden mb-6">
              {pipelineStages.map((stage) => (
                <div 
                  key={stage.name}
                  className={`${stage.color} transition-all`}
                  style={{ width: `${(stage.count / totalPipeline) * 100}%` }}
                />
              ))}
            </div>
            <div className="grid grid-cols-5 gap-4">
              {pipelineStages.map((stage) => (
                <div key={stage.name} className="text-center">
                  <div className={`w-3 h-3 rounded-full ${stage.color} mx-auto mb-2`} />
                  <p className="text-xs text-slate-500 uppercase tracking-wider">{stage.name}</p>
                  <p className="text-2xl font-bold text-white">{stage.count}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="bg-slate-900 border-slate-800">
          <CardHeader>
            <CardTitle className="text-white">Quick Actions</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {quickActions.map((action) => (
              <Button
                key={action.label}
                onClick={action.onClick}
                className={`w-full justify-start ${action.color}`}
              >
                <action.icon className="w-4 h-4 mr-3" />
                {action.label}
              </Button>
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="bg-slate-900 border-slate-800">
        <CardHeader>
          <CardTitle className="text-white flex items-center gap-2">
            <Clock className="w-5 h-5 text-blue-400" />
            Recent Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {stats.recent_activities && stats.recent_activities.length > 0 ? (
            <div className="space-y-4">
              {stats.recent_activities.slice(0, 5).map((activity, idx) => (
                <div key={idx} className="flex items-center gap-4 p-3 rounded-lg bg-slate-800/50">
                  <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center">
                    {activity.type === 'lead' && <TrendingUp className="w-5 h-5 text-green-400" />}
                    {activity.type === 'contact' && <Users className="w-5 h-5 text-blue-400" />}
                    {activity.type === 'task' && <CheckSquare className="w-5 h-5 text-amber-400" />}
                    {!['lead', 'contact', 'task'].includes(activity.type) && <Activity className="w-5 h-5 text-slate-400" />}
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-white">{activity.description || activity.title}</p>
                    <p className="text-xs text-slate-500">{activity.time || 'Just now'}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Activity className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-400">No recent activity</p>
              <p className="text-sm text-slate-500 mt-1">Start by adding contacts or leads</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
